import Scanner, { ScannedItem } from "@/common/components/scan";
import { RECALL_RESPONSE } from "@/common/constants";
import { useApiCallHandling } from "@/hooks/useApiCallHandling";
import { useState } from "react";
import styled from "styled-components";
import { KeyValueContainer } from "@/common/components/key-value-wrapper";
import { H3 } from "@/common/elements";
import { KeyValueObj } from "@/common/types";
import { IAddedBatch } from "@/common/types/others";
import {
  getProductForDelivery,
  groupItemsByBatch,
  handleScannedItem,
  scanTableHeaders,
  scanTableKeys,
} from "../data";
import { useNotification } from "@/common/components/Notifications/NotificationProvider";
import { ISource } from ".";

const ScanWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 2rem;
  padding: 2rem;

  @media (max-width: ${({ theme }) => theme.screen.mobile}) {
    padding: 2rem 0.5rem;
  }

  .text-wrapper {
    padding: 2rem 2rem 1rem;
    text-align: center;
    border-bottom: 1px solid ${({ theme }) => theme.border.light};

    h2 {
      font-size: 1.5rem;
      font-weight: 600;
      color: ${({ theme }) => theme.text.primary};
      text-transform: capitalize;

      @media (max-width: ${({ theme }) => theme.screen.mobile}) {
        font-size: 1.1rem;
      }
    }
  }

  .containerWrapper {
    background-color: white;
    padding: 2rem 3rem;
    width: 100%;
    .top-wrapper {
      width: 100%;
      display: flex;
      justify-content: flex-end;
    }
    .cta {
      margin-top: 1rem;
      display: flex;
      justify-content: center;
    }
  }
`;

interface IAutoScanner {
  source: string;
  data?: KeyValueObj;
  handleProductSelected: (
    batchData: IAddedBatch[],
    orderProduct: KeyValueObj,
    serials: string[],
  ) => void;
}

function AutoScanner({ source, data, handleProductSelected }: IAutoScanner) {
  const makeRequest = useApiCallHandling();
  const notify = useNotification();
  const [loadingRecall, setLoadingRecall] = useState<boolean>(false);
  const product = getProductForDelivery(source as ISource, data as KeyValueObj);
  const [serialList, setSerialList] = useState<string[]>([]);

  const productKeyValueData = [
    {
      title: "Brand Name",
      value: product?.brand_name,
    },
    {
      title: "Strength",
      value: product?.strength,
    },
    {
      title: "Total Quantity Required",
      value: product?.quantity || "",
    },
  ];

  const handleScanSerial = (serial: string): Promise<ScannedItem[]> => {
    return new Promise((resolve) => {
      makeRequest(
        RECALL_RESPONSE + `/identify?serial_number=${serial}`,
        "GET",
        {},
        (data) => {
          const scannedItems = handleScannedItem(data);
          setSerialList((prev) => [...prev, serial]);
          resolve(scannedItems);
        },
        () => resolve([]),
        () => {},
      );
    });
  };

  const handleProceed = (items: ScannedItem[]) => {
    if (items?.length === 0) {
      notify({
        type: "ERROR",
        message: `Total item cannot be empty`,
      });
      setLoadingRecall(false);
      return;
    }

    const batchItem = groupItemsByBatch(items);

    handleProductSelected(batchItem, product, serialList);
  };

  return (
    <ScanWrapper>
      <div className="text-wrapper">
        <h2>Scan To Dispatch</h2>
      </div>
      <div className="containerWrapper">
        <div>
          <H3>Product Details</H3>
          <KeyValueContainer data={productKeyValueData} />
        </div>
        <Scanner
          tableHeaders={scanTableHeaders}
          tableKeys={scanTableKeys}
          onScanSerial={handleScanSerial}
          onProceed={handleProceed}
          loadingProceed={loadingRecall}
          proceedLabel="Continue"
        />
      </div>
    </ScanWrapper>
  );
}

export default AutoScanner;
